import { Box } from '@mui/material';
import { IconButton } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { useCallback, useEffect } from 'react';
import { useAiAgentApiFacade } from '../../../../../apis/facades/useAiAgentApiFacade';
import { telemetry } from '../../../../../telemetry';
import { ProcessMiningPrompt } from './ProcessMiningPrompt';
import { AiChatMessage } from '../../../../common/ai-chat/aiChatTypes';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import { usePageHelpers } from '../usePageHelpers';
import { DateTime } from 'luxon';
import { useProcessMining } from '../useProcessMining';
import { translateNewConfigurationTypeKeysToOldVersion } from '../../../../documentation/dependencies/helpers';
import {
  addDefaultPositions,
  createCanvasConnections,
  useAddMetadataElementToCanvas,
} from '../../../funnel-map-flow/metadata-elements/useAddMetadataElementToCanvas';
import { setIsPageLoading } from '../../../../../reducers/canvasPageStateReducer';
import { useDispatch } from 'react-redux';
import { addMetadataElementsToFunnelMap } from '../../../funnel-map-flow/metadata-elements/addMetadataElementsToFunnelMap';
import assignDefaultColorsAndIcons from '../../../funnel-map-flow/metadata-elements/metadataElementsColorsAndIcons';
import { useFunnelMapApiFacade } from '../../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import { setInitialFunnelMap } from '../../../../../reducers/united-canvas/unitedCanvasReducer';
import { normalizeFunnelMapPositions } from '../../../funnel-map-flow/normalizeFunnelMapPositions';
import { useAutoSaveContext } from '../../../funnel-map-flow/auto-save/AutoSaveContext';
import { ProcessMiningLoader } from './ProcessMiningLoader';

export const ProcessMiningPanel = ({ onClose }: { onClose?: () => void }) => {
  const { post_processMining } = useAiAgentApiFacade();
  const { funnelMapId } = usePageHelpers();
  const { get_funnelMap } = useFunnelMapApiFacade();
  const dispatch = useDispatch();
  const { getMetadataElementsWithConnections } = useAddMetadataElementToCanvas(true);
  const { funnelMapVersion } = useAutoSaveContext();

  const { isLoading, isCompleted, error, setLoading, setError, message } = useProcessMining(
    funnelMapId || '',
  );

  const onComplete = useCallback(async () => {
    if (isCompleted && !!funnelMapId) {
      try {
        const funnelMap = (await get_funnelMap(funnelMapId, true)) as FunnelMap;
        funnelMapVersion.current = funnelMap.version;
        dispatch(setIsPageLoading({ isPageLoading: false }));

        const hasMetadataElements =
          funnelMap.metadataElements && Object.keys(funnelMap.metadataElements).length > 0;

        if (hasMetadataElements) {
          const metadataElements = Object.entries(funnelMap.metadataElements);
          const metadataElementsData = funnelMap.metadataElementsData;

          const metadataElementsForEnrichment = metadataElements.map(([id, metadataElement]) => ({
            ...metadataElement,
            ...metadataElementsData[id],
            name: metadataElementsData[id]?.name || '',
            type: translateNewConfigurationTypeKeysToOldVersion(
              metadataElementsData[id]?.type || '',
            ),
            parentType: translateNewConfigurationTypeKeysToOldVersion(
              metadataElementsData[id]?.type || '',
            ),
            icon: metadataElement.icon ?? undefined,
          }));

          const metadataElementsWithConnections = await getMetadataElementsWithConnections({
            metadataElements: metadataElementsForEnrichment,
          });

          assignDefaultColorsAndIcons(metadataElementsWithConnections);
          // Add default positions to elements that don't have one
          const metadataElementsWithPosition = addDefaultPositions(metadataElementsWithConnections);

          // Create canvas connections from metadata relationships
          const connections = createCanvasConnections(metadataElementsWithConnections, funnelMap);

          addMetadataElementsToFunnelMap(funnelMap, metadataElementsWithPosition, connections);
          dispatch(setInitialFunnelMap(normalizeFunnelMapPositions(funnelMap)));
          dispatch(setIsPageLoading({ isPageLoading: false }));
        }
      } catch (error) {
        telemetry.captureError(new Error('Failed to enrich metadata elements:'), error);
      }
    }
  }, [
    dispatch,
    funnelMapId,
    getMetadataElementsWithConnections,
    get_funnelMap,
    isCompleted,
    funnelMapVersion,
  ]);

  useEffect(() => {
    if (isCompleted) {
      onComplete();
    }
  }, [isCompleted, onComplete]);

  const onSendMessage = useCallback(
    async (messageContent: string) => {
      if (!funnelMapId) {
        return;
      }

      const message: AiChatMessage = {
        role: ChatCompletionRequestMessageRole.USER,
        content: messageContent,
        createdAt: DateTime.now().toISO(),
      };

      try {
        setLoading(true);
        // Response will come via websocket
        await post_processMining(funnelMapId, { message });
      } catch (error) {
        telemetry.captureError(error);
        setError(`Couldn't generate results. Try again with a new prompt.`);
      }
    },
    [post_processMining, funnelMapId, setLoading, setError],
  );

  if (isLoading) {
    return <ProcessMiningLoader />;
  }

  return (
    <Box
      sx={{
        width: '600px',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        padding: 0,
        position: 'relative',
      }}
    >
      {onClose && (
        <Box
          sx={{
            position: 'absolute',
            top: '4px',
            right: 0,
            zIndex: 2,
          }}
        >
          <IconButton variant="system" onClick={onClose} size="tiny">
            <Close />
          </IconButton>
        </Box>
      )}
      <ProcessMiningPrompt
        onSendMessage={onSendMessage}
        error={error}
        loading={isLoading}
        message={message}
      />
    </Box>
  );
};
